// library imports
import {
  Button,
  Card,
  Modal,
  Tooltip,
  theme,
  Tag,
  Flex,
  Select,
  Space,
  DatePicker,
  Typography,
} from "antd";
import { useState, useEffect, useContext, useReducer } from "react";
import dayjs from "dayjs";
import { SearchOutlined, UndoOutlined } from "@ant-design/icons";
// chart imports
import { Line } from "@ant-design/plots";
// component imports
import DataTable from "../common/DataTable";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";
// config imports
import {
  DARK_BLUE,
  DARK_GREEN,
  DARK_THEME_DARK_BLUE,
} from "../../configs/configs";
// style imports
import "../../styles/Alarm/AlarmPage.css";

const { Text } = Typography;
const { useToken } = theme;

function reducer(state, action) {
  switch (action.type) {
    case "setStartDate":
      return { ...state, startDate: action.date };
    case "setEndDate":
      return { ...state, endDate: action.date };
    case "setDeviceSelected":
      return {
        ...state,
        deviceSelected: action.device,
      };
    default:
      return "Unrecognized command";
  }
}

function HistoryTable({ tableData, onViewClick }) {
  const truncateText = (text, limit) => {
    const words = text.split("");
    const check = words.length > limit;
    return check ? words.slice(0, limit).join("") + "..." : text;
  };
  const historyTableColumns = [
    {
      title: "No.",
      dataIndex: "number",
      key: "number",
    },
    {
      title: "Device",
      dataIndex: "Device",
      key: "Device",
    },
    {
      title: "String No.",
      dataIndex: "StringNo",
      key: "StringNo",
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
      render: (text) => (
        <Tooltip title={text}>{truncateText(text, 20)}</Tooltip>
      ),
    },
    {
      title: "Start",
      dataIndex: "StartDateTime",
      key: "StartDateTime",
    },
    {
      title: "End",
      dataIndex: "EndDateTime",
      key: "EndDateTime",
    },
    {
      title: "Action",
      dataIndex: "Action",
      key: "Action",
      render: (_, record, index) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            onClick={() => {
              onViewClick(record);
            }}
          >
            View
          </Button>
        </div>
      ),
    },
  ];

  return (
    <DataTable table_data={tableData} table_column={historyTableColumns} />
  );
}

function HistoryModal({
  modelOpen,
  modalClose,
  isLoading,
  title,
  chart_data,
  isLightTheme,
}) {
  const lineChartConfig = {
    autoFit: true,
    data: chart_data?.length ? chart_data : [],
    xField: "time",
    yField: "data",
    seriesField: "type",
    smooth: false,
    legend: {
      layout: "horizontal",
      position: "bottom",
    },
    point: false,
    xAxis: {
      title: {
        text: "Time",
        style: {
          fontSize: 16,
        },
      },
    },
    yAxis: {
      title: {
        text: "Parameter",
        style: {
          fontSize: 16,
        },
      },
      label: {
        formatter: (val) => parseFloat(val).toFixed(2),
      },
    },
    tooltip: {
      formatter: (datum) => ({
        name: datum.type,
        value: datum.data.toFixed(2), // 2 decimal places
      }),
    },
    color: isLightTheme
      ? ["grey", DARK_BLUE, DARK_GREEN]
      : ["grey", DARK_THEME_DARK_BLUE, DARK_GREEN],
  };

  return (
    <Modal
      open={modelOpen}
      onCancel={() => modalClose()}
      footer={null}
      style={{ maxWidth: "90vw", minWidth: "70vw", height: "80vh" }}
    >
      <Card
        title={title?.length ? title : ""}
        style={{ marginTop: "30px" }}
        loading={isLoading}
      >
        <Line {...lineChartConfig} />
      </Card>
    </Modal>
  );
}

function HistoricalPvCondition() {
  // token
  const { token } = useToken();

  // fetch wrapper
  const [fetchWrapper] = useFetch();

  // load index context
  const { siteSelected, isLightTheme, notificationApi } =
    useContext(IndexContext);

  // loading state
  const [isLoading, setIsLoading] = useState(false);

  // modal open state
  const [historyModal, setHistoryModal] = useState(false);

  // table data
  const [historyTableData, setHistoryTableData] = useState([]);

  // chart data
  const [historyChartData, setHistoryChartData] = useState([]);

  // view record ID (to display correct modal title)
  const [viewRecordID, setViewRecordID] = useState(0);

  // device selection
  const [deviceList, setDeviceList] = useState([]);

  // historical pv condition filter
  const [historicalPvConditionFilter, setHistoricalPvConditionFilter] =
    useState([]);

  // site commissioning date
  const [siteCommissioningDate, setSiteCommissioningDate] = useState(dayjs());

  const initialState = {
    startDate: dayjs(
      Math.max(
        dayjs().hour(0).minute(0).second(0).add(-14, "day").valueOf(),
        dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf()
      )
    ),
    endDate: dayjs().hour(0).minute(0).second(0).add(1, "day"),
    deviceSelected: [],
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  // dispatch functions
  function handleStartDate(date) {
    dispatch({ type: "setStartDate", date: date });
  }
  function handleEndDate(date) {
    dispatch({ type: "setEndDate", date: date });
  }
  function handleDeviceChange(device) {
    dispatch({ type: "setDeviceSelected", device: device });
  }

  const onViewClick = (record) => {
    setHistoryModal(true);
    setViewRecordID(record.ID);
    getChartData(record.ID);
  };

  const onModalClose = () => {
    setHistoryModal(false);
  };

  const onDismissClick = () => {
    console.log("Dismiss Button");
  };

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  /* API to get device list*/
  function getDeviceList() {
    fetchWrapper({
      endpoint_url: "pv_condition/getDeviceList",
      method: "GET",
      set_content_type_header: false,
      params: { site_id: siteSelected },
      onSuccess: (response) => {
        setDeviceList(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get device list.",
          description: response.statusText,
        });
        setDeviceList([]);
      },
    });
  }

  /** API to get active record */
  function getHistoryRecords() {
    setIsLoading(true);

    fetchWrapper({
      endpoint_url: "pv_condition/getAllHistory",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        device_id_list:
          historicalPvConditionFilter.length !== 0
            ? historicalPvConditionFilter.device_id_list.length === 0
              ? deviceList.map((item) => item.value)
              : historicalPvConditionFilter.device_id_list
            : deviceList.map((item) => item.value),
        start_date:
          historicalPvConditionFilter.length !== 0
            ? historicalPvConditionFilter.start_date
            : state.startDate.format("YYYY-MM-DD HH:mm:ss"), // this condition is catered for the initial calling of the API where search button is not clicked yet
        end_date:
          historicalPvConditionFilter.length !== 0
            ? historicalPvConditionFilter.end_date
            : state.endDate.format("YYYY-MM-DD HH:mm:ss"),
      },
      onSuccess: (response) => {
        setHistoryTableData(response.data);
      },
      onFinish: () => {
        setIsLoading(false);
      },
    });
  }

  /** API to get chart data */
  function getChartData(idx) {
    setIsLoading(true);

    fetchWrapper({
      endpoint_url: "pv_condition/getChartData",
      method: "POST",
      body: {
        record_id: idx,
      },
      onSuccess: (response) => {
        setHistoryChartData(response.data);
      },
      onFinish: () => {
        setIsLoading(false);
      },
    });
  }

  let modal_title = "";
  if (historyTableData?.length) {
    if (viewRecordID > 0) {
      const viewed_record = historyTableData.find(
        (element) => element.ID === viewRecordID
      );
      modal_title = viewed_record.Device + " - " + viewed_record.Parameter;
    }
  }

  useEffect(() => {
    // call the API only when device list has been fetched
    if (deviceList.length > 0) {
      if (siteSelected) {
        getHistoryRecords();
      }
    }
  }, [deviceList, siteSelected, historicalPvConditionFilter]);

  useEffect(() => {
    if (siteSelected) {
      getDeviceList();
      getSiteCommissioningDate();
    }
  }, [siteSelected]);

  // when site commissioning date changes, need to update start date and historical pv condition filter
  useEffect(() => {
    handleStartDate(
      dayjs(
        Math.max(
          dayjs().hour(0).minute(0).second(0).add(-14, "day").valueOf(),
          dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf()
        )
      )
    );

    setHistoricalPvConditionFilter({
      start_date: dayjs(
        Math.max(
          dayjs().hour(0).minute(0).second(0).add(-14, "day").valueOf(),
          dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf()
        )
      ).format("YYYY-MM-DD HH:mm:ss"),
      end_date: state.endDate.format("YYYY-MM-DD HH:mm:ss"),
      device_id_list: state.deviceSelected,
    });
  }, [siteCommissioningDate]);

  return (
    <>
      <Flex
        className="mb-4"
        gap={8}
        wrap={true}
        align="flex-start"
        justify="space-between"
      >
        <Space.Compact block style={{ width: "fit-content" }}>
          <Button disabled>Device</Button>
          <Select
            placeholder={
              <Tag color={token.colorPrimary} className="pl-3 pr-3">
                <Text style={{ color: "white" }}>All</Text>
              </Tag>
            }
            className="historical-alarm-device-selection w-100"
            value={state.deviceSelected}
            options={deviceList}
            mode={"multiple"}
            allowClear
            tagRender={({ label, onClose, closable }) => (
              <Tag
                color={token.colorPrimary}
                closable={closable}
                onClose={onClose}
                style={{
                  marginRight: 3,
                }}
              >
                <Text style={{ color: "white" }}>{label}</Text>
              </Tag>
            )}
            onChange={(value) => {
              handleDeviceChange(value);
            }}
          />
        </Space.Compact>

        <Space.Compact block style={{ width: "fit-content" }}>
          <Button disabled>Start</Button>
          <DatePicker
            showTime
            showHour
            showMinute
            showSecond={false}
            allowClear={false}
            value={state.startDate}
            placement="bottomLeft"
            defaultValue={state.startDate}
            needConfirm={false}
            onChange={(date) => {
              if (date > state.endDate) {
                handleStartDate(state.endDate);
                handleEndDate(date);
              } else {
                handleStartDate(date);
              }
            }}
            maxDate={state.endDate}
            minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
          />
          <Button disabled>End</Button>
          <DatePicker
            showTime
            showHour
            showMinute
            showSecond={false}
            allowClear={false}
            value={state.endDate}
            defaultValue={state.endDate}
            needConfirm={false}
            onChange={(date) => {
              if (date < state.startDate) {
                handleEndDate(state.startDate);
                handleStartDate(date);
              } else {
                handleEndDate(date);
              }
            }}
            minDate={state.startDate}
            placement="topLeft"
          />
        </Space.Compact>

        <Flex gap={8}>
          <Tooltip title="Reset to default setting">
            <Button
              icon={<UndoOutlined />}
              onClick={() => {
                handleStartDate(
                  dayjs(
                    Math.max(
                      dayjs()
                        .hour(0)
                        .minute(0)
                        .second(0)
                        .add(-14, "day")
                        .valueOf(),
                      dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf()
                    )
                  )
                );
                handleEndDate(
                  dayjs().hour(0).minute(0).second(0).add(1, "day")
                );
                handleDeviceChange([]);
                setHistoricalPvConditionFilter({
                  start_date: dayjs()
                    .hour(0)
                    .minute(0)
                    .second(0)
                    .add(-14, "day")
                    .format("YYYY-MM-DD HH:mm:ss"),
                  end_date: dayjs()
                    .hour(0)
                    .minute(0)
                    .second(0)
                    .add(1, "day")
                    .format("YYYY-MM-DD HH:mm:ss"),
                  device_id_list: [],
                });
              }}
            />
          </Tooltip>
          <Tooltip title="Search">
            <Button
              icon={<SearchOutlined />}
              onClick={() => {
                setHistoricalPvConditionFilter({
                  start_date: state.startDate.format("YYYY-MM-DD HH:mm:ss"),
                  end_date: state.endDate.format("YYYY-MM-DD HH:mm:ss"),
                  device_id_list: state.deviceSelected,
                });
              }}
            />
          </Tooltip>
        </Flex>
      </Flex>

      <HistoryTable
        tableData={historyTableData}
        onViewClick={onViewClick}
        onDismissClick={onDismissClick}
      />
      <HistoryModal
        modelOpen={historyModal}
        modalClose={onModalClose}
        isLoading={isLoading}
        title={modal_title}
        chart_data={historyChartData}
        isLightTheme={isLightTheme}
      />
    </>
  );
}

export default HistoricalPvCondition;
